import { useEffect, useState } from "react";
import * as api from "../../api/client";
import { pixelTheme } from "../../theme/pixel";
import { bevelShadow } from "../../theme/styles";
import { CLAUDE_CODE_PRESETS, ModelPicker } from "./ModelPicker";

// Server-wide defaults bar — generation model, validator model,
// prompt version. Self-contained: loads on mount, saves on every
// change. Used by the Generate tab and the Card Editor's AI Fill
// flow so the same controls drive every `:generate_card` job. The
// rules validator itself always runs (server-enforced); only its
// model is configurable here.
//
// Pulled out of `pages/AdminPage.tsx` (where it was inlined into
// AdminGenerateTab) so the editor can render the same widget without
// duplicating the load/save plumbing.

export function buildGenerateSettingsSavePayload(
  current: api.GenerateSettings,
  patch: Partial<api.GenerateSettings>,
): Partial<api.GenerateSettings> {
  const merged = { ...current, ...patch };
  const payload: Partial<api.GenerateSettings> = {
    prompt_version: merged.prompt_version,
  };
  const model = merged.model.trim();
  const validatorModel = merged.validator_model.trim();
  if (model) payload.model = model;
  if (validatorModel) payload.validator_model = validatorModel;
  return payload;
}

export function shouldSaveGenerateModelValue(
  next: string,
  current: string,
  force: boolean,
): boolean {
  const trimmed = next.trim();
  return trimmed !== "" && (force || trimmed !== current);
}

export function GenerateSettingsBar({
  onError,
  description = "Server-wide defaults persisted in the database.",
}: {
  /** Bubble up load/save failures to the host page's banner. */
  onError?: (message: string | null) => void;
  description?: string;
}) {
  const theme = pixelTheme;
  const [model, setModel] = useState("");
  const [validatorModel, setValidatorModel] = useState("");
  const [promptVersion, setPromptVersion] = useState("v1");
  const [promptVersions, setPromptVersions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [settings, versions] = await Promise.all([
          api.getGenerateSettings(),
          api.listPromptVersions(),
        ]);
        if (cancelled) return;
        setModel(settings.model);
        setValidatorModel(settings.validator_model);
        setPromptVersion(settings.prompt_version);
        setPromptVersions(versions.versions);
      } catch (err) {
        if (cancelled) return;
        onError?.(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  async function saveSettings(patch: Partial<api.GenerateSettings>) {
    setSaving(true);
    onError?.(null);
    try {
      const settings = await api.updateGenerateSettings(
        buildGenerateSettingsSavePayload(
          {
            model,
            validator_model: validatorModel,
            prompt_version: promptVersion,
          },
          patch,
        ),
      );
      if (Object.prototype.hasOwnProperty.call(patch, "model")) {
        setModel(settings.model);
      }
      if (Object.prototype.hasOwnProperty.call(patch, "validator_model")) {
        setValidatorModel(settings.validator_model);
      }
      if (Object.prototype.hasOwnProperty.call(patch, "prompt_version")) {
        setPromptVersion(settings.prompt_version);
      }
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function saveModelValue(
    next: string,
    key: "model" | "validator_model" = "model",
    force = false,
  ) {
    const current = key === "model" ? model : validatorModel;
    if (!shouldSaveGenerateModelValue(next, current, force)) {
      if (key === "model") setModel(next);
      else setValidatorModel(next);
      return;
    }
    await saveSettings({ [key]: next.trim() });
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
        padding: 8,
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
      }}
    >
      <span style={labelStyle(theme)}>DEFAULT MODEL</span>
      <ModelPicker
        value={model}
        onChange={(next) => {
          setModel(next);
          if (next && !next.includes("/")) {
            void saveModelValue(next);
          }
        }}
        presets={CLAUDE_CODE_PRESETS}
        disabled={saving}
      />
      {model.includes("/") && (
        <button
          onClick={() => void saveModelValue(model, "model", true)}
          disabled={saving || !model.trim()}
          style={savePillStyle(theme)}
        >
          {saving ? "SAVING…" : "SAVE"}
        </button>
      )}
      <span style={labelStyle(theme)} title="The rules validator always runs; pick which model judges the cards.">
        VALIDATOR MODEL
      </span>
      <ModelPicker
        value={validatorModel}
        onChange={(next) => {
          setValidatorModel(next);
          if (next && !next.includes("/")) {
            void saveModelValue(next, "validator_model");
          }
        }}
        presets={CLAUDE_CODE_PRESETS}
        disabled={saving}
      />
      {validatorModel.includes("/") && (
        <button
          onClick={() =>
            void saveModelValue(validatorModel, "validator_model", true)
          }
          disabled={saving || !validatorModel.trim()}
          style={savePillStyle(theme)}
        >
          {saving ? "SAVING…" : "SAVE"}
        </button>
      )}
      <select
        value={promptVersion}
        onChange={(e) => {
          const next = e.target.value;
          setPromptVersion(next);
          void saveSettings({ prompt_version: next });
        }}
        disabled={saving || promptVersions.length === 0}
        style={selectInlineStyle(theme)}
      >
        {promptVersions.length === 0 ? (
          <option value={promptVersion}>{promptVersion.toUpperCase()}</option>
        ) : (
          promptVersions.map((v) => (
            <option key={v} value={v}>
              {v.toUpperCase()}
            </option>
          ))
        )}
      </select>
      <span style={{ color: theme.inkFaint, fontSize: 12 }}>{description}</span>
    </div>
  );
}

function labelStyle(theme: typeof pixelTheme): React.CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
    color: theme.inkDim,
  };
}

function savePillStyle(theme: typeof pixelTheme): React.CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
    padding: "6px 10px",
    background: theme.accent,
    color: theme.bgDeep,
    border: `2px solid ${theme.accent}`,
    cursor: "pointer",
  };
}

function selectInlineStyle(theme: typeof pixelTheme): React.CSSProperties {
  return {
    padding: "8px 10px",
    background: theme.panel,
    color: theme.ink,
    border: `2px solid ${theme.panelEdge}`,
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
  };
}
